"use client";

import { CheckCircle2, X, Stamp, FileText, Info, Car, ShieldCheck, Music, Sparkles, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { TabsContent } from "@/components/ui/tabs";
import { CarDetails } from "@/lib/actions/car-details";

interface CarDetailsContentProps {
  car: CarDetails;
  activeTab: string;
}

export function CarDetailsContent({ car, activeTab }: CarDetailsContentProps) {
  // Helper function to animate list items
  const listItemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
      },
    }),
  };

  return (
    <>
      <TabsContent value="features" className="space-y-6">
        {car.carFeature ? (
          <>
            {car.carFeature.interiorFeatures && car.carFeature.interiorFeatures.length > 0 && (
              <motion.div
                initial="hidden"
                animate={activeTab === "features" ? "visible" : "hidden"}
                variants={listItemVariants}
                className="space-y-2"
              >
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <div className="p-1 rounded-full bg-primary/10">
                    <Car className="h-4 w-4 text-primary" />
                  </div>
                  Interior Features
                </h3>
                <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                  {car.carFeature.interiorFeatures.map((feature, index) => (
                    <motion.li 
                      key={index}
                      variants={listItemVariants}
                      className="flex items-start gap-2"
                    >
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </motion.div>
            )}
            
            {car.carFeature.exteriorFeatures && car.carFeature.exteriorFeatures.length > 0 && (
              <motion.div
                initial="hidden"
                animate={activeTab === "features" ? "visible" : "hidden"}
                variants={listItemVariants}
                className="space-y-2"
              >
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <div className="p-1 rounded-full bg-primary/10">
                    <Car className="h-4 w-4 text-primary" />
                  </div>
                  Exterior Features
                </h3>
                <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                  {car.carFeature.exteriorFeatures.map((feature, index) => (
                    <motion.li 
                      key={index}
                      variants={listItemVariants}
                      className="flex items-start gap-2"
                    >
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </motion.div>
            )}
            
            {car.carFeature.safetyFeatures && car.carFeature.safetyFeatures.length > 0 && (
              <motion.div
                initial="hidden"
                animate={activeTab === "features" ? "visible" : "hidden"}
                variants={listItemVariants}
                className="space-y-2"
              >
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <div className="p-1 rounded-full bg-primary/10">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                  </div>
                  Safety Features
                </h3>
                <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                  {car.carFeature.safetyFeatures.map((feature, index) => (
                    <motion.li 
                      key={index}
                      variants={listItemVariants}
                      className="flex items-start gap-2"
                    >
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </motion.div>
            )}
            
            {car.carFeature.entertainmentFeatures && car.carFeature.entertainmentFeatures.length > 0 && (
              <motion.div
                initial="hidden"
                animate={activeTab === "features" ? "visible" : "hidden"}
                variants={listItemVariants}
                className="space-y-2"
              >
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <div className="p-1 rounded-full bg-primary/10">
                    <Music className="h-4 w-4 text-primary" />
                  </div>
                  Entertainment Features
                </h3>
                <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                  {car.carFeature.entertainmentFeatures.map((feature, index) => (
                    <motion.li 
                      key={index}
                      variants={listItemVariants}
                      className="flex items-start gap-2"
                    >
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </motion.div>
            )}
            
            {car.carFeature.convenienceFeatures && car.carFeature.convenienceFeatures.length > 0 && (
              <motion.div
                initial="hidden"
                animate={activeTab === "features" ? "visible" : "hidden"}
                variants={listItemVariants}
                className="space-y-2"
              >
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <div className="p-1 rounded-full bg-primary/10">
                    <Sparkles className="h-4 w-4 text-primary" />
                  </div>
                  Convenience Features
                </h3>
                <motion.ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                  {car.carFeature.convenienceFeatures.map((feature, index) => (
                    <motion.li 
                      key={index}
                      variants={listItemVariants}
                      className="flex items-start gap-2"
                    >
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </motion.ul>
              </motion.div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <div className="bg-muted/30 mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-3">
              <Info className="h-6 w-6 text-muted-foreground/60" />
            </div>
            <h3 className="text-lg font-medium">No Feature Information</h3>
            <p className="text-muted-foreground mt-1">
              Information about this vehicle's features is not available at the moment.
            </p>
          </div>
        )}
      </TabsContent>

      <TabsContent value="specs" className="space-y-6">
        {car.carSpecification ? (
          <motion.div
            initial="hidden"
            animate={activeTab === "specs" ? "visible" : "hidden"}
            variants={listItemVariants}
            className="relative rounded-xl border border-primary/10 overflow-hidden"
          >
            <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
            <div className="p-6 relative grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {Object.entries(car.carSpecification)
                .filter(([key]) => !['id', 'carId', 'createdAt', 'updatedAt'].includes(key))
                .map(([key, value]) => {
                  if (!value) return null;
                  
                  // Format the key from camelCase to readable format
                  const formattedKey = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase())
                    .replace(/Id$/, 'ID');
                  
                  return (
                    <div key={key} className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{formattedKey}</span>
                      <span className="font-medium text-right">{value}</span>
                    </div>
                  );
                })}
            </div>
          </motion.div>
        ) : (
          <div className="text-center py-8">
            <div className="bg-muted/30 mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-3">
              <Info className="h-6 w-6 text-muted-foreground/60" />
            </div>
            <h3 className="text-lg font-medium">No Specification Information</h3>
            <p className="text-muted-foreground mt-1">
              Information about this vehicle's specifications is not available at the moment.
            </p>
          </div>
        )}
      </TabsContent>

      <TabsContent value="ownership" className="space-y-6">
        {car.carOwnership ? (
          <motion.div
            initial="hidden"
            animate={activeTab === "ownership" ? "visible" : "hidden"}
            variants={listItemVariants}
            className="relative rounded-xl border border-primary/10 overflow-hidden"
          >
            <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
            <div className="p-6 relative grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {Object.entries(car.carOwnership)
                .filter(([key]) => !['id', 'carId', 'createdAt', 'updatedAt'].includes(key))
                .map(([key, value]) => {
                  if (value === null || value === undefined) return null;
                  
                  // Format the key from camelCase to readable format
                  const formattedKey = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase())
                    .replace(/Id$/, 'ID');
                  
                  // Handle boolean values
                  if (typeof value === 'boolean') {
                    value = value ? 'Yes' : 'No';
                  }
                  
                  return (
                    <div key={key} className="flex items-center justify-between gap-4">
                      <span className="text-muted-foreground">{formattedKey}</span>
                      <span className="font-medium text-right">{value}</span>
                    </div>
                  );
                })}
            </div>
          </motion.div>
        ) : (
          <div className="text-center py-8">
            <div className="bg-muted/30 mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-3">
              <Info className="h-6 w-6 text-muted-foreground/60" />
            </div>
            <h3 className="text-lg font-medium">No Ownership Information</h3>
            <p className="text-muted-foreground mt-1">
              Information about this vehicle's ownership history is not available at the moment.
            </p>
          </div>
        )}
      </TabsContent>

      <TabsContent value="documents" className="space-y-6">
        {car.carDocuments && car.carDocuments.length > 0 ? (
          <motion.ul
            initial="hidden"
            animate={activeTab === "documents" ? "visible" : "hidden"}
            variants={listItemVariants}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            {car.carDocuments.map((doc, index) => (
              <motion.li
                key={index}
                variants={listItemVariants}
                className="relative rounded-xl border border-primary/10 overflow-hidden hover:border-primary/30 transition-colors"
              >
                <a 
                  href={doc.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">{doc.title}</h4>
                      <p className="text-sm text-muted-foreground truncate">{doc.fileName || doc.type || 'Document'}</p>
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </a>
              </motion.li>
            ))}
          </motion.ul>
        ) : (
          <div className="text-center py-8">
            <div className="bg-muted/30 mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-3">
              <Info className="h-6 w-6 text-muted-foreground/60" />
            </div>
            <h3 className="text-lg font-medium">No Documents Available</h3>
            <p className="text-muted-foreground mt-1">
              This vehicle doesn't have any documents uploaded at the moment.
            </p>
          </div>
        )}
      </TabsContent>
    </>
  );
} 